import { connect } from 'react-redux'
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from 'react-places-autocomplete'
import PropTypes from 'prop-types'
import React from 'react'
import clsx from 'clsx'
import StateStorage from 'lib/state-storage'
import { withI18next } from 'lib/i18n/withI18next'
import Loader from 'components/loader'
import Icon from 'components/common/icon'
import ICONS, { ICON_SIZE } from 'components/common/icon/const'
import Typo from 'constants/typography'
import { segmentPomeloPickupInteracted } from 'lib/segment'
import env from 'config/env'
import BackdropModal from 'components/portal/backdrop-modal'
import BlockedLocationModal from 'components/modal/blocked-location-modal'
import Coachmark from 'components/common/coachmark'
import { COACHMARK_POSITION } from 'components/common/coachmark/const'
import TextField from 'components/common/textfield'
import styles from './style.scss'

const DEBOUNCE_TIME = 2000
let timer

const GEO_STATE = {
  denied: 'denied',
  granted: 'granted',
  prompt: 'prompt',
}
const GOOGLE_MAP_SCRIPT_LOAD_CALLBACK = 'onMapScriptLoaded'
const GOOGLE_MAP_URL = `https://maps.googleapis.com/maps/api/js?key=${env.GOOGLE_API_KEY}&libraries=places&callback=${GOOGLE_MAP_SCRIPT_LOAD_CALLBACK}`
const GOOGLE_MAP_SCRIPT_ID = 'google_map_script'

class SearchInput extends React.PureComponent {
  searchInputEl = React.createRef()

  constructor() {
    super()

    this.loadGoogleMapScript(GOOGLE_MAP_SCRIPT_ID, GOOGLE_MAP_URL)
    this.state = {
      geoAvailable: 'geolocation' in navigator,
      geoFetching: false,
      geoState: GEO_STATE.prompt,
      inputLocked: false,
      showCoachMark: false,
      showLocationPermissionModal: false,
      value: '',
    }
    this.geoOptions = {
      enableHighAccuracy: true,
      maximumAge: 5000, // Location cache lifespan
      timeout: 30000, // Will have to return after it takes too long
    }
  }

  componentDidMount() {
    const { geoAvailable } = this.state
    // Slight delay to enable fade in effect
    setTimeout(() => {
      const isCoachMarkShown = StateStorage.isPinCoachMarkShown()

      if (!isCoachMarkShown) {
        this.setState({ showCoachMark: true })
      }
    }, 250)

    // Automatically set base location to user's location if the permission is already granted
    if (geoAvailable && navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((status) => {
        if (status.state === GEO_STATE.granted) {
          this.geoGetUserLocation()
        }

        this.setState({ geoState: status.state })
      })
    }
  }

  isGoogleMapsLoaded() {
    return window && window.google && window.google.maps
  }

  loadGoogleMapScript(scriptId, src) {
    if (document.getElementById(scriptId) || this.isGoogleMapsLoaded()) {
      return
    }

    const script = document.createElement('script')
    script.setAttribute('src', src)
    script.setAttribute('id', scriptId)
    script.setAttribute('async', true)
    script.setAttribute('defer', true)
    document.body.appendChild(script)
  }

  onCoachMarkClosed = () => {
    StateStorage.setPinCoachMarkShown(true)
    this.setState({ showCoachMark: false })
  }

  onSuggestionSelected(placeName) {
    const { geoUpdate, showUnavailable } = this.props
    this.setState({
      inputLocked: true,
      value: placeName,
      geoState: GEO_STATE.prompt,
    })

    // Clear all the distances
    geoUpdate(null)

    /* istanbul ignore if */
    if (this.searchInputEl.current) {
      this.searchInputEl.current.blur()
    }

    geocodeByAddress(placeName)
      .then((res) => getLatLng(res[0]))
      .then((latLng) =>
        geoUpdate({ latitude: latLng.lat, longitude: latLng.lng }),
      )
      .catch(() => showUnavailable())
  }

  geoGetUserLocation() {
    const { geoFetching, geoState } = this.state
    const { geoUpdate } = this.props
    if (geoFetching || geoState === GEO_STATE.denied) {
      return undefined
    }

    this.setState({ geoFetching: true })
    navigator.geolocation.getCurrentPosition(
      (res) => {
        this.setState({
          geoFetching: false,
          geoState: GEO_STATE.granted,
          value: '',
        })
        geoUpdate(res.coords)
      },
      () => this.setState({ geoFetching: false, geoState: GEO_STATE.denied }),
      this.geoOptions,
    )
    return undefined
  }

  clearLocation() {
    const { geoUpdate } = this.props
    this.setState({
      inputLocked: true,
      value: '',
      geoState: GEO_STATE.prompt,
    })
    geoUpdate(null)
  }

  handleLocationClick = () => {
    const { isFromCheckout, cart } = this.props
    const { geoState } = this.state

    if (isFromCheckout) {
      const segmentData = {
        interaction_type: 'nearby',
        id_cart: cart?.metadata.id_cart,
      }
      segmentPomeloPickupInteracted(segmentData)
    }

    if (geoState && geoState !== GEO_STATE.denied) {
      this.geoGetUserLocation()
    } else {
      this.toggleShowLocationPermissionModal()
    }
  }

  toggleShowLocationPermissionModal = (e) => {
    if (e?.preventDefault) {
      /* istanbul ignore next */
      e.preventDefault()
    }

    this.setState(({ showLocationPermissionModal }) => ({
      showLocationPermissionModal: !showLocationPermissionModal,
    }))
  }

  renderLocationTracking() {
    const { t } = this.props
    const { geoState, showCoachMark, showLocationPermissionModal } = this.state

    return (
      <Coachmark
        onClose={this.onCoachMarkClosed}
        position={COACHMARK_POSITION.TOP_RIGHT}
        text={t('Allow location tracking to find nearby locations')}
        isOpen={showCoachMark}
        className="SI__coachmark"
        wrapperClassName="SI__location-tracking-area"
        closeBtn={
          <span className={clsx('search__coachmark-close-btn', Typo.caption)}>
            {t('got it')}
          </span>
        }
        disableLeaveListener
        disableEnterListener
      >
        {geoState && geoState === GEO_STATE.granted ? (
          <div className="SI__location-tracking-area tracked-disabled">
            <hr className="SI__separator-line" />
            <Icon
              className="location-tracking-icon"
              src={ICONS.webCurrentLocationDisabled}
              size={ICON_SIZE.medium}
            />
            <span
              className={clsx(
                'SI__span-nearyby__tracked-disabled',
                Typo.overline,
              )}
            >
              {t('Tracked')}
            </span>
          </div>
        ) : (
          <React.Fragment>
            <hr className="SI__separator-line" />
            <Icon
              onClick={this.handleLocationClick}
              className="location-tracking-icon"
              src={ICONS.webCurrentLocation}
              size={ICON_SIZE.small}
            />
            <BackdropModal
              onBackdropClick={this.toggleShowLocationPermissionModal}
              isOpen={showLocationPermissionModal}
            >
              <BlockedLocationModal
                close={this.toggleShowLocationPermissionModal}
              />
            </BackdropModal>
          </React.Fragment>
        )}
      </Coachmark>
    )
  }

  // cannot find a proper way to mock suggestion from react-places-autocomplete yet
  /* istanbul ignore next */
  renderSuggestion(item, index, getSuggestionItemProps) {
    const { t } = this.props

    const suggestionProps = getSuggestionItemProps(item, {
      className: `search__result-item${item.active ? ' hover' : ''}`,
      onMouseDown: () => this.onSuggestionSelected(item.description),
    })

    return (
      <li {...suggestionProps} key={`tmp-${index}`}>
        <Icon
          className="icon"
          src={ICONS.pickUpLocation}
          size={ICON_SIZE.medium}
        />
        <div className={clsx(Typo.caption, 'suggestion-description')}>
          {t(item.description)}
        </div>
      </li>
    )
  }

  handlePickUpInteracted(res) {
    const { cart } = this.props
    clearTimeout(timer)
    timer = setTimeout(() => {
      const segmentData = {
        interaction_type: 'search',
        query: res,
        id_cart: cart?.metadata.id_cart,
      }
      segmentPomeloPickupInteracted(segmentData)
    }, DEBOUNCE_TIME)
  }

  handleInputChange(res) {
    if (res && res.length > 0) {
      this.handlePickUpInteracted(res)
      this.setState({ value: res, inputLocked: false })
    }

    if (res.length === 0) {
      this.clearLocation()
    }
  }

  render() {
    const { state } = this
    const { inputLocked, value } = state

    const { t } = this.props
    const searchActiveClasses = clsx({
      search__input: true,
      selected: inputLocked,
    })
    const searchResultClasses = clsx({
      search__result: true,
      active: !inputLocked,
    })

    // This bounds data is the same with the apps
    if (this.isGoogleMapsLoaded() && !this.searchOptions) {
      this.searchOptions = {
        bounds: new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(13.447785, 100.103168),
          new window.google.maps.LatLng(14.340953, 101.31422),
        ),
      }
    }

    return (
      <PlacesAutocomplete
        value={t(value)}
        searchOptions={this.searchOptions}
        onChange={(res) => this.handleInputChange(res)}
        onSelect={(res) => this.onSuggestionSelected(res)}
        onError={(_, clearSuggestions) => clearSuggestions()}
        googleCallbackName={GOOGLE_MAP_SCRIPT_LOAD_CALLBACK}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <React.Fragment>
            <style jsx>{styles}</style>
            <div className={searchActiveClasses}>
              <TextField
                fullWidth
                className="search-field"
                prefix={
                  <Icon
                    className="placeholder-search-icon"
                    src={ICONS.search}
                    size={ICON_SIZE.medium}
                  />
                }
                postfix={
                  value ? (
                    <Icon
                      className="close-icon"
                      src={ICONS.close}
                      size={ICON_SIZE.medium}
                      onClick={() => this.clearLocation()}
                    />
                  ) : undefined
                }
                hideUnderline
                inputRef={this.searchInputEl}
                inputProps={getInputProps({
                  type: 'text',
                  placeholder: t('Try your office postal code.'),
                })}
              />
              {this.renderLocationTracking()}
            </div>
            <ul className={searchResultClasses}>
              {loading && (
                <li className="search__result-item">
                  <i className="icon loader">
                    <Loader micro />
                  </i>
                  {t('Loading...')}
                </li>
              )}
              {suggestions &&
                suggestions.length > 0 &&
                suggestions.map((item, index) =>
                  this.renderSuggestion(item, index, getSuggestionItemProps),
                )}
            </ul>
          </React.Fragment>
        )}
      </PlacesAutocomplete>
    )
  }
}

SearchInput.defaultProps = {
  isFromCheckout: false,
  cart: PropTypes.shape({
    metadata: PropTypes.shape({
      id_cart: PropTypes.number,
    }),
  }),
  geoUpdate: undefined,
  showUnavailable: undefined,
}

SearchInput.propTypes = {
  geoUpdate: PropTypes.func,
  showUnavailable: PropTypes.func,
  t: PropTypes.func.isRequired,
  isFromCheckout: PropTypes.bool,
  cart: PropTypes.shape({
    metadata: PropTypes.shape({
      id_cart: PropTypes.number,
    }),
  }),
}

export { SearchInput }

const Extended = withI18next()(SearchInput)

export default connect(
  /* istanbul ignore next */
  (state) => ({
    cart: state.cart.payload,
  }),
)(Extended)
